import {Grid} from '@logistics-frontend/blocks'
import {useMediaQuery} from '@logistics-frontend/blocks/legacy'
import {Alert} from '@logistics-frontend/blocks/lpc/alert'
import {AdaptiveDialog, Dialog, DialogContent, DialogTitleWithClose} from '@logistics-frontend/blocks/lpc/dialog'
import {Divider} from '@logistics-frontend/blocks/lpc/divider'
import {FormControlLabel} from '@logistics-frontend/blocks/lpc/form-control-label'
import {ChevronRightIcon} from '@logistics-frontend/blocks/lpc/icons'
import {Portal} from '@logistics-frontend/blocks/lpc/portal'
import {RadioGroup} from '@logistics-frontend/blocks/lpc/radio'
import RadioControl from '@logistics-frontend/blocks/lpc/radio/RadioControl'
import {Snackbar, SnackbarOrigin} from '@logistics-frontend/blocks/lpc/snackbar'
import {theme} from '@logistics-frontend/blocks/lpc/theme'
import {Buttonv2 as Button, Typographyv2 as Typography} from '@logistics-frontend/blocks/v2'
import {useI18n} from '@logistics-frontend/core/i18n'
import {PaymentMethod, PaymentMethodType, StartupResponse} from '@yandex-taxi/js-integration-api'
import {AsyncState, useAuthorizationState, usePayment, usePaymentsQuery} from '@yandex-taxi/react-integration-api'
import {FrameMessageError} from '@yandex-trust-sdk/base/dist-cjs/types'
import clsx from 'clsx'
import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {CLASS_FOR_SCROLL_TO_ERROR} from '../../../consts'
import {UNSTABLE__useZonePointUpdate} from '../../../shared/buttons/hooks'
import AddPaymentFrame from '../../../shared/buttons/payment/AddPaymentFrame'
import {usePassportGoalCallback, usePaymentGoalCallback} from '../../../shared/metrika'
import {isPaymentMethod, toPaymentMethod} from '../../../shared/utils'
import {AddCardButton} from './AddCardButton'
import PaymentOption from './PaymentOption'
import {classes} from './Payments.clasess'
import {PaymentMethodsResponse} from './types'

const position: SnackbarOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
}

const mobilePosition: SnackbarOrigin = {
  vertical: 'top',
  horizontal: 'center',
}

const MAX_CARDS_AMOUNT = 5
const control = <RadioControl />

const Payments: React.FC = () => {
  const t = useI18n()
  const [visibleEditCards, setVisibleEditCards] = useState<boolean>(false)
  const [visibleSelectPayment, setVisibleSelectPayment] = useState<boolean>(false)
  const [err, setError] = useState<string>()
  const goalCallback = usePaymentGoalCallback()
  const updateZonePoint = UNSTABLE__useZonePointUpdate()
  const [{value, error, touched}, {setValue}] = usePayment()
  const {data} = usePaymentsQuery() as AsyncState<PaymentMethodsResponse>
  const cardGoal = usePassportGoalCallback('LinkCard')
  const matches = useMediaQuery(theme.breakpoints.down('sm'))

  const hideEditCardsHandler = useCallback(() => setVisibleEditCards(false), [])
  const clearErrorHandler = useCallback(() => setError(undefined), [])

  const hideSelectPaymentHandler = useCallback(() => {
    setVisibleSelectPayment(false)
  }, [])

  const showSelectPaymentHandler = useCallback(() => {
    setVisibleSelectPayment(true)
  }, [])

  const showEditCardsHandler = useCallback(() => {
    setVisibleEditCards(true)
    cardGoal()
  }, [cardGoal])

  const setErrorHandler = useCallback(
    (msg: FrameMessageError) => {
      hideEditCardsHandler()
      setError(msg.reason)
    },
    [hideEditCardsHandler],
  )

  const successHandler = useCallback(() => {
    hideEditCardsHandler()
    updateZonePoint()
  }, [hideEditCardsHandler, updateZonePoint])

  const changeHandler = useCallback(
    (_, stringValue: string) => {
      setValue(toPaymentMethod(stringValue), true)
      goalCallback()
    },
    [setValue, goalCallback],
  )

  const {methods, lastMethodID} = useMemo(() => {
    const lastMethodID = data?.last_payment_method?.id
    const methods: PaymentMethod[] = data?.methods.filter(isPaymentMethod) || []

    if (methods.some(m => m.id === lastMethodID)) {
      return {
        lastMethodID,
        methods,
      }
    }

    return {
      methods,
    }
  }, [data])

  const isLimitCardAmount =
    methods.filter(method => method.type === PaymentMethodType.CARD).length + (data?.unverified_cards?.length || 0) >=
    MAX_CARDS_AMOUNT

  // если не выбран способ оплаты и есть последний использованный, то установим его по умолчанию
  useEffect(() => {
    if (!value && lastMethodID) {
      changeHandler(undefined, lastMethodID)
    }
  }, [changeHandler, lastMethodID, value])

  const defaultValue = methods.length && !value.id ? toPaymentMethod('cash') : undefined

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue)
    }
  }, [setValue, defaultValue])

  if (!methods.length) {
    return null
  }

  const selectedMethod = methods.find(method => method.id === value.id)
  const helperText = touched && error

  return (
    <>
      <Grid
        alignItems="center"
        className={classes.button}
        component="button"
        justify="space-between"
        onClick={showSelectPaymentHandler}
        wrap="nowrap"
        container
      >
        <Typography className={classes.titleText} variant="caption1">
          {t('order.form.payment.payment_method_title', 'Способ оплаты')}
        </Typography>

        <Grid alignItems="center" className={classes.method} justify="center" wrap="nowrap" item>
          {selectedMethod ? (
            <PaymentOption method={selectedMethod} />
          ) : (
            <Typography className={classes.methodText} thickness="regular" variant="body2">
              {t('order.form.payment.not_chosen', 'Не выбрано')}
            </Typography>
          )}
          {helperText && (
            <Typography className={clsx(classes.helperText, CLASS_FOR_SCROLL_TO_ERROR)} variant="caption1">
              {helperText}
            </Typography>
          )}
          <ChevronRightIcon style={{minWidth: 24}} />
        </Grid>
      </Grid>
      <Divider />
      <AdaptiveDialog maxWidth="xs" onClose={hideSelectPaymentHandler} open={!!visibleSelectPayment} fullWidth>
        <DialogTitleWithClose onClose={hideSelectPaymentHandler} littleCloseIcon>
          {t('order.form.payment.payment_methods_dialog_title', 'Способы оплаты')}
        </DialogTitleWithClose>
        <DialogContent>
          <RadioGroup onChange={changeHandler} value={`${value.id}`}>
            {methods.map(method => (
              <FormControlLabel
                key={method.id}
                className={classes.label}
                control={control}
                label={<PaymentOption method={method} />}
                labelPlacement="start"
                value={method.id}
              />
            ))}
          </RadioGroup>
          <AddCardButton disabled={isLimitCardAmount} onClick={showEditCardsHandler} />
          <Button className={classes.closeButton} onClick={hideSelectPaymentHandler} variant="default" fullWidth>
            {t('order.form.payment.close_dialog', 'Закрыть')}
          </Button>
        </DialogContent>
      </AdaptiveDialog>
      <Portal>
        <Snackbar
          anchorOrigin={matches ? mobilePosition : position}
          autoHideDuration={10000}
          className={classes.snackbar}
          onClose={clearErrorHandler}
          open={!!err}
        >
          <Alert severity="error" variant="filled">
            {err}
          </Alert>
        </Snackbar>
      </Portal>
      {visibleEditCards && (
        <Dialog maxWidth="md" fullWidth open>
          <DialogTitleWithClose onClose={hideEditCardsHandler}>
            {t('order.form.payment.add_payment_card', 'Добавить платёжную карту')}
          </DialogTitleWithClose>
          <DialogContent>
            <AddPaymentFrame onAbort={hideEditCardsHandler} onError={setErrorHandler} onSuccess={successHandler} />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

const AuthorizationPayments: React.FC = () => {
  // только авторизованные польователи могут ходить за способами оплаты
  const {data} = useAuthorizationState<StartupResponse>()

  if (data?.authorization_status === 'authorized') {
    return <Payments />
  }

  return null
}

export default AuthorizationPayments
